import type { ComponentPropsWithoutRef, Ref } from 'react';
import {
  Badge,
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@monksflow/monks-ui';

import { CustomAgentChip } from '../CustomAgentChip';
import { ToolBadge } from '../ToolBadge';

import type { ToolCardProps } from './ToolCard.types';
import { toolCardV4Variants, type ToolCardV4VariantProps } from './ToolCard.v4.variants';
import { useTranslation } from 'react-i18next';

export interface ToolCardV4Props
  extends
    Omit<ComponentPropsWithoutRef<'div'>, 'title' | 'onClick' | 'color'>,
    Omit<ToolCardProps, 'color'>,
    ToolCardV4VariantProps {
  /** React 19 ref-as-prop: forwarded to the underlying `<div>` (monks-ui Card). */
  ref?: Ref<HTMLDivElement>;
}

export function ToolCardV4({
  onClick,
  title,
  description,
  tag,
  agentId,
  disabled = false,
  disabledReason,
  disabledTooltip,
  statusLabel,
  visibility,
  className,
  ref,
  ...rest
}: ToolCardV4Props) {
  const { t } = useTranslation('common');

  const activateOnEnterOrSpace = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  };

  const tooltipText = disabled ? disabledTooltip || undefined : undefined;
  const badgeText =
    disabled || statusLabel
      ? statusLabel || disabledReason || t('components.toolCard.comingSoon')
      : null;

  return (
    <Card
      ref={ref}
      variant="outline"
      data-slot="tool-card"
      data-disabled={disabled}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t('components.toolCard.ariaLabel', { title })}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onClick}
      onKeyDown={activateOnEnterOrSpace}
      title={tooltipText}
      className={toolCardV4Variants({ disabled, className })}
      {...rest}
    >
      <CardHeader className="p-0">
        <ToolBadge toolId={agentId} category={tag} size="xl" />
        {(badgeText || visibility) && (
          <CardAction className="flex items-center gap-xs">
            {visibility && <CustomAgentChip visibility={visibility} />}
            {badgeText && <Badge variant="secondary">{badgeText}</Badge>}
          </CardAction>
        )}
      </CardHeader>

      <CardContent className="flex flex-col gap-1 p-0">
        <CardTitle className="overflow-hidden text-ellipsis line-clamp-2 text-xl leading-6 font-medium tracking-tight text-foreground">
          {title || t('components.toolCardV4.untitledAgent')}
        </CardTitle>
        {description && (
          <CardDescription className="overflow-hidden text-ellipsis line-clamp-2 text-sm text-foreground-alt">
            {description}
          </CardDescription>
        )}
      </CardContent>
    </Card>
  );
}

ToolCardV4.displayName = 'ToolCardV4';
